import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import {
  addPulse,
  addQuickWin,
  addReview,
  DEFAULT_PULSE_INTERVAL_DAYS,
  DEFAULT_TEAM_ADOPTION_PATH,
  emptyTeamState,
  IDEA_GENESIS,
  nextTrackState,
  PULSE_SCALE_MAX,
  pulseDue,
  pulseScaleMax,
  pulseTrend,
  reviewTally,
  setTrackNote,
  setTrackState,
  TRACK_STATE_LABELS,
  trackSummary,
} from '../../lib/team';
import { extractTeamFromFile, upsertTeamNode } from '../../lib/team_org';
import { directoryOfPath } from '../../lib/dashboard';

// How much of each append-only log the page shows at once. The file holds all of
// it; this is only about not turning the page into a wall (concept §4.2, §4.3).
const LOG_SHOWN = 8;

const EMPTY_REVIEW_DRAFT = { change: '', verified: '', decision: null };

const TeamDashboard = ({ syncBackendClient, teamAdoptionPath, pulseIntervalDays }) => {
  const path = (teamAdoptionPath || '').trim() || DEFAULT_TEAM_ADOPTION_PATH;

  // Fixed at mount, exactly like the other three dashboards: a page left open
  // across midnight keeps dating its entries to the day it was opened on until
  // it is refreshed.
  const today = useMemo(() => new Date(), []);

  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [noteDraft, setNoteDraft] = useState(null); // { key, text }
  const [winDraft, setWinDraft] = useState('');
  const [reviewDraft, setReviewDraft] = useState(EMPTY_REVIEW_DRAFT);
  const [pulseDraft, setPulseDraft] = useState('');

  // The raw text of the file, so a write only ever replaces the team node and
  // leaves the rest of the file exactly as it was. A ref, not state, so two
  // quick edits both build on the write that came just before them.
  const rawRef = useRef(null);
  const existsRef = useRef(false);
  // Chained so two quick edits become two orderly commits instead of two racing
  // ones - the GitLab commit API rejects concurrent branch updates.
  const queueRef = useRef(Promise.resolve());

  const load = useCallback(async () => {
    if (!syncBackendClient) {
      setLoadError('Not connected to a sync backend.');
      setLoaded(true);
      return;
    }

    setLoadError(null);
    setLoaded(false);

    try {
      // Listed first, like the contract dashboard: knowing whether the file is
      // there avoids a failed fetch on a record that has not been started yet -
      // and stops a network error from being mistaken for "no file", which would
      // let the next write overwrite a file that does exist.
      const listing = await syncBackendClient
        .getDirectoryListing(directoryOfPath(path))
        .catch(() => null);

      const present = new Set();
      ((listing && listing.listing) || []).forEach((file) => {
        const filePath = file.get ? file.get('path') : file.path;
        if (filePath) present.add(filePath);
      });

      const contents = present.has(path) ? await syncBackendClient.getFileContents(path) : null;

      rawRef.current = contents;
      existsRef.current = contents !== null;

      setState(extractTeamFromFile(contents));
      setLoaded(true);
    } catch (error) {
      console.error('Could not read the team record:', error);
      setLoadError(error.message);
      setState(null);
      setLoaded(true);
    }
  }, [syncBackendClient, path]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const persist = useCallback(
    (nextState) => {
      setState(nextState);
      setSaveError(null);

      const nextContents = upsertTeamNode(rawRef.current, nextState);
      rawRef.current = nextContents;

      queueRef.current = queueRef.current.then(async () => {
        try {
          if (existsRef.current) {
            await syncBackendClient.updateFile(path, nextContents);
          } else {
            await syncBackendClient.createFile(path, nextContents);
            existsRef.current = true;
          }
        } catch (error) {
          setSaveError(`Could not save ${path}: ${error.message || error}`);
        }
      });
    },
    [syncBackendClient, path]
  );

  const canWrite = !!syncBackendClient;

  // A file with no team node yet is shown as an empty record rather than as a
  // wall: the page is usable straight away, and the file is only created once
  // there is actually something to write into it.
  const record = useMemo(() => state || emptyTeamState(), [state]);

  const summary = useMemo(() => trackSummary(record.track), [record.track]);
  const tally = useMemo(() => reviewTally(record.reviews), [record.reviews]);
  const trend = useMemo(() => pulseTrend(record.pulses), [record.pulses]);
  const scaleMax = useMemo(() => pulseScaleMax(record.pulses), [record.pulses]);
  const due = useMemo(() => pulseDue(record.pulses, today, pulseIntervalDays), [
    record.pulses,
    today,
    pulseIntervalDays,
  ]);

  // All three lines, or nothing: the gate is minimal in ceremony and
  // non-negotiable in substance (concept §4.3). A bare checkbox is exactly what
  // Tang et al. (2024) found gets rubber-stamped, so there is no way to log a
  // shipped change here without saying what was checked and who decided.
  const reviewComplete =
    !!reviewDraft.change.trim() && !!reviewDraft.verified.trim() && !!reviewDraft.decision;

  const handleCycleState = (entry) => {
    if (!canWrite) return;
    persist(setTrackState(record, entry.key, nextTrackState(entry.state)));
  };

  const handleSaveNote = () => {
    if (!canWrite || !noteDraft) return;
    persist(setTrackNote(record, noteDraft.key, noteDraft.text));
    setNoteDraft(null);
  };

  const handleLogWin = () => {
    if (!canWrite || !winDraft.trim()) return;
    persist(addQuickWin(record, { text: winDraft, today }));
    setWinDraft('');
  };

  const handleLogReview = () => {
    if (!canWrite || !reviewComplete) return;
    persist(addReview(record, { ...reviewDraft, today }));
    setReviewDraft(EMPTY_REVIEW_DRAFT);
  };

  const handleLogPulse = () => {
    if (!canWrite) return;
    const score = Number(pulseDraft);
    if (!Number.isFinite(score) || score <= 0) return;
    persist(addPulse(record, { score, today }));
    setPulseDraft('');
  };

  const header = (
    <div className="team__header">
      <h3 className="team__title">Team</h3>
      <div className="team__header__actions">
        <button className="team__header__action" onClick={() => setReloadToken((n) => n + 1)}>
          <i className="fas fa-sync-alt" /> Refresh
        </button>
        <Link className="team__header__action" to="/dashboard/contract">
          <i className="fas fa-file-signature" /> Contract
        </Link>
        <Link className="team__header__action" to="/dashboard/week">
          <i className="fas fa-chart-line" /> This week
        </Link>
        <Link className="team__header__action" to="/dashboard/focus">
          <i className="fas fa-bullseye" /> Focus
        </Link>
        <Link className="team__header__action" to="/dashboard/formation">
          <i className="fas fa-seedling" /> Formation
        </Link>
        <Link className="team__header__action" to="/dashboard/review">
          <i className="fas fa-moon" /> Review
        </Link>
        <Link className="team__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
      </div>
    </div>
  );

  if (!loaded) {
    return (
      <div className="team-container">
        {header}
        <div className="team__loading">
          <i className="fas fa-spinner fa-spin" /> Reading {path}...
        </div>
      </div>
    );
  }

  return (
    <div className="team-container">
      {header}

      {!!loadError && <div className="team__error">{loadError}</div>}
      {!!saveError && <div className="team__error">{saveError}</div>}

      <div className="team__intro">
        One person's private cockpit for leading a change — nobody on the team logs in here, and
        every number on this page is something you noticed and typed. No velocity, no throughput:
        the 2024 DORA data has those rising for individuals while team delivery stability falls.
        {!state && (
          <>
            {' '}
            Nothing is written to <code>{path}</code> yet — the first change below creates it.
          </>
        )}
      </div>

      {/* ── §4.1 The change track ──────────────────────────────────────────── */}

      <div className="team__section-title">
        <span>The change track</span>
        <span className="team__section-title__note">
          {summary.active} active · {summary.anchored} anchored · of {summary.total}
        </span>
      </div>

      <div className="team__track">
        {record.track.map((entry) => (
          <div className={`team__track__row team__track__row--${entry.state}`} key={entry.key}>
            <button
              className={`team__track__state team__track__state--${entry.state}`}
              disabled={!canWrite}
              title="Not started → active → anchored. Nothing here is gated on anything else."
              onClick={() => handleCycleState(entry)}
            >
              {TRACK_STATE_LABELS[entry.state]}
            </button>

            <div className="team__track__body">
              <div className="team__track__label">{entry.label}</div>
              {!!entry.caution && <div className="team__track__caution">{entry.caution}</div>}

              {noteDraft && noteDraft.key === entry.key ? (
                <div className="team__inline-form">
                  <input
                    className="textfield"
                    type="text"
                    value={noteDraft.text}
                    autoFocus
                    placeholder="What is actually being done for this"
                    onChange={(event) => setNoteDraft({ ...noteDraft, text: event.target.value })}
                    onKeyDown={(event) => event.key === 'Enter' && handleSaveNote()}
                  />
                  <button className="team__action" onClick={handleSaveNote}>
                    Save
                  </button>
                  <button className="btn btn-passive" onClick={() => setNoteDraft(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className={`team__track__note${entry.note ? '' : ' team__track__note--empty'}`}
                  disabled={!canWrite}
                  onClick={() => setNoteDraft({ key: entry.key, text: entry.note })}
                >
                  {entry.note || 'one line: what is actually being done'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── §4.2 Quick wins ────────────────────────────────────────────────── */}

      <div className="team__section-title">
        <span>Quick wins</span>
        <span className="team__section-title__note">{record.quickWins.length} logged</span>
      </div>

      <div className="team__hint">
        Small, real, visible wins from the new way of working — the one Kotter mechanism with
        peer-reviewed grounding of its own (Weick 1984). Append-only: a mistyped line is corrected
        in <code>{path}</code> itself, not here.
      </div>

      {canWrite && (
        <div className="team__inline-form">
          <input
            className="textfield"
            type="text"
            value={winDraft}
            placeholder="Nobody had to fix the generated diff — shipped same day"
            onChange={(event) => setWinDraft(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleLogWin()}
          />
          <button className="team__action" disabled={!winDraft.trim()} onClick={handleLogWin}>
            Log win
          </button>
        </div>
      )}

      {record.quickWins.length ? (
        <ul className="team__log">
          {record.quickWins
            .slice(-LOG_SHOWN)
            .reverse()
            .map((win) => (
              <li className="team__log__entry" key={`${win.date}-${win.text}`}>
                <span className="team__log__date">{win.date}</span>
                <span>{win.text}</span>
              </li>
            ))}
        </ul>
      ) : (
        <div className="team__empty">Nothing logged yet.</div>
      )}

      {/* ── §4.3 The review gate ───────────────────────────────────────────── */}

      <div className="team__section-title">
        <span>The review gate</span>
      </div>

      <div className="team__hint">
        Per shipped change of any real size: what was <em>actually</em> checked, and whether the
        call was the group's or one person accepting an AI suggestion. Nothing blocks merging except
        this gate — no second gate, no extra approvers. All three lines are required here, because a
        bare checkbox is what gets rubber-stamped (Tang et al. 2024), and a solo-accepted suggestion
        is the overconfidence Perry et al. (2023) measured directly.
      </div>

      {tally.shipped > 0 && (
        <div className="team__numbers">
          <strong>{tally.withNote}</strong> of <strong>{tally.shipped}</strong> shipped changes say
          what was checked · <strong>{tally.groupDecided}</strong> were group decisions ·{' '}
          <strong>{tally.soloDecided}</strong> one person's
          {tally.unnamed > 0 && (
            <>
              {' '}
              · <strong>{tally.unnamed}</strong> never named either way
            </>
          )}
        </div>
      )}

      {canWrite && (
        <div className="team__form">
          <input
            className="textfield"
            type="text"
            value={reviewDraft.change}
            placeholder="What shipped — e.g. release-notes generator"
            onChange={(event) => setReviewDraft({ ...reviewDraft, change: event.target.value })}
          />
          <input
            className="textfield"
            type="text"
            value={reviewDraft.verified}
            placeholder='What was actually verified — not "reviewed", what you checked'
            onChange={(event) => setReviewDraft({ ...reviewDraft, verified: event.target.value })}
          />
          <div className="team__form__row">
            <div className="team__choice">
              <button
                className={`team__choice__option${
                  reviewDraft.decision === 'group' ? ' team__choice__option--chosen' : ''
                }`}
                onClick={() => setReviewDraft({ ...reviewDraft, decision: 'group' })}
              >
                the group decided
              </button>
              <button
                className={`team__choice__option${
                  reviewDraft.decision === 'solo' ? ' team__choice__option--chosen' : ''
                }`}
                onClick={() => setReviewDraft({ ...reviewDraft, decision: 'solo' })}
              >
                one person accepted it
              </button>
            </div>
            <button className="team__action" disabled={!reviewComplete} onClick={handleLogReview}>
              Log the gate
            </button>
          </div>
        </div>
      )}

      {record.reviews.length ? (
        <ul className="team__log">
          {record.reviews
            .slice(-LOG_SHOWN)
            .reverse()
            .map((review) => (
              <li className="team__log__entry" key={`${review.date}-${review.change}`}>
                <span className="team__log__date">{review.date}</span>
                <span>
                  <span className="team__log__change">{review.change}</span>
                  <span
                    className={`team__badge team__badge--${review.decision || 'unnamed'}`}
                    title="How the call was made"
                  >
                    {review.decision === 'group'
                      ? 'group'
                      : review.decision === 'solo'
                      ? 'solo'
                      : 'not named'}
                  </span>
                  <div className="team__log__verified">
                    {review.verified || 'no verification line — added by hand?'}
                  </div>
                </span>
              </li>
            ))}
        </ul>
      ) : (
        <div className="team__empty">Nothing logged yet.</div>
      )}

      {/* ── §4.4 Idea genesis ──────────────────────────────────────────────── */}

      <div className="team__section-title">
        <span>How ideas actually get better</span>
        <span className="team__section-title__note">a reminder, not a form</span>
      </div>

      <div className="team__cards">
        {IDEA_GENESIS.map((item) => (
          <div className="team__card" key={item.text}>
            <div className="team__card__text">{item.text}</div>
            <div className="team__card__why">{item.why}</div>
            <div className="team__card__strength">{item.strength}</div>
          </div>
        ))}
      </div>

      {/* ── §4.5 Team pulse ────────────────────────────────────────────────── */}

      <div className="team__section-title">
        <span>Team pulse</span>
        <span className="team__section-title__note">every {due.intervalDays} days</span>
      </div>

      <div className={`team__pulse${due.due ? ' team__pulse--due' : ''}`}>
        <div className="team__pulse__status">
          {due.lastDate === null
            ? 'No reading yet. Run a short anonymous pulse with the team — outside organice, however fits — and log the aggregate.'
            : due.due
            ? `Last reading ${due.lastDate}, ${due.daysSince} days ago. Time to ask again.`
            : `Last reading ${due.lastDate}, ${due.daysSince} days ago. Next one in ${
                due.intervalDays - due.daysSince
              } days.`}
        </div>

        {trend.length > 1 && (
          <svg
            className="team__pulse__chart"
            viewBox="0 0 100 40"
            preserveAspectRatio="none"
            role="img"
            aria-label={`Pulse readings, ${trend.length} of them, most recently ${
              trend[trend.length - 1].score
            } of ${scaleMax}`}
          >
            <line className="team__pulse__axis" x1="0" y1="39.5" x2="100" y2="39.5" />
            <polyline
              className="team__pulse__line"
              points={trend
                .map((reading, index) => {
                  const x = (index / (trend.length - 1)) * 100;
                  const y = 40 - Math.min(1, reading.score / scaleMax) * 38;
                  return `${x.toFixed(2)},${y.toFixed(2)}`;
                })
                .join(' ')}
            />
          </svg>
        )}

        {trend.length > 0 && (
          <ul className="team__pulse__readings">
            {trend
              .slice(-LOG_SHOWN)
              .reverse()
              .map((reading) => (
                <li key={reading.date}>
                  <span className="team__log__date">{reading.date}</span>
                  <strong>{reading.score}</strong>
                  <span className="team__pulse__scale"> / {scaleMax}</span>
                </li>
              ))}
          </ul>
        )}

        {canWrite && (
          <div className="team__inline-form">
            <input
              className="textfield default-deadline-value-textfield"
              type="number"
              min="0"
              max={scaleMax}
              step="0.1"
              value={pulseDraft}
              placeholder={`${PULSE_SCALE_MAX}`}
              onChange={(event) => setPulseDraft(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleLogPulse()}
            />
            <button className="team__action" disabled={!pulseDraft.trim()} onClick={handleLogPulse}>
              Log reading
            </button>
            <span className="team__hint team__hint--inline">
              the aggregate the team gave you, on a {PULSE_SCALE_MAX}-point scale — never your own
              estimate of how safe they feel
            </span>
          </div>
        )}
      </div>

      <div className="team__footer">
        Stored as one <code>* Team :team-adoption:</code> node in <code>{path}</code>, one commit
        per change. See <code>doc/team-dashboard-concept.org</code> for what each section rests on —
        and for which of it is honestly thin.
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  teamAdoptionPath: state.base.get('teamAdoptionPath') || DEFAULT_TEAM_ADOPTION_PATH,
  pulseIntervalDays:
    state.base.get('teamPulseIntervalDays') === undefined
      ? DEFAULT_PULSE_INTERVAL_DAYS
      : state.base.get('teamPulseIntervalDays'),
});

export default connect(mapStateToProps)(TeamDashboard);
